import React, { useState, useEffect } from 'react';
import { Send, Search, ShieldAlert, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import { filterAuthorities } from '../../utils/fuzzyMatch';

interface Authority {
	id: number;
	userId: number;
	authority_phoneNumber: number;
	name: string;
	description: string;
	profilepicURL?: string;
	// Admin-assigned search aliases (e.g. "plumber, tap, leak"), matched
	// alongside `name` by fuzzyMatch. See Authority.keywords on the backend.
	keywords?: string;
}

interface ForwardReportModalProps {
	isOpen: boolean;
	onClose: () => void;
	reportId: number | null;
	// Either of these can identify the current viewer's authority row. We accept
	// both so the comparison still works if one is missing.
	currentAuthorityPhone?: number;
	currentUserId?: number;
	onForwardSuccess: (reportId: number) => void;
}

const ForwardReportModal: React.FC<ForwardReportModalProps> = ({
	isOpen,
	onClose,
	reportId,
	currentAuthorityPhone,
	currentUserId,
	onForwardSuccess,
}) => {
	const [authorities, setAuthorities] = useState<Authority[]>([]);
	const [isLoadingAuths, setIsLoadingAuths] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedAuthorityId, setSelectedAuthorityId] = useState<number | null>(null);
	const [reason, setReason] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const modalRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!isOpen) return;
			if (e.key === 'Escape') {
				onClose();
				return;
			}
			if (e.key === 'Tab') {
				const focusableElements = modalRef.current?.querySelectorAll(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
				) as NodeListOf<HTMLElement>;

				if (!focusableElements || focusableElements.length === 0) return;

				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === firstElement || document.activeElement === document.body) {
						e.preventDefault();
						lastElement.focus();
					}
				} else {
					if (document.activeElement === lastElement || document.activeElement === document.body) {
						e.preventDefault();
						firstElement.focus();
					}
				}
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleKeyDown);
			setTimeout(() => {
				const searchInput = modalRef.current?.querySelector('input');
				if (searchInput && document.activeElement !== searchInput) searchInput.focus();
			}, 10);
		}
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, onClose]);

	useEffect(() => {
		if (isOpen) {
			fetchAuthorities();
		} else {
			// Reset state on close
			setSearchQuery('');
			setSelectedAuthorityId(null);
			setReason('');
			setError(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const fetchAuthorities = async () => {
		setIsLoadingAuths(true);
		setError(null);
		try {
			const response = await fetch(`${BACKEND_URL}/api/authorities`, {
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			if (!response.ok) throw new Error('Failed to load authorities');

			const data: Authority[] = await response.json();
			const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
			// Keep the current authority in the list — but the row is rendered
			// disabled (with a "You" tag) below so they can't pick themselves.
			setAuthorities(sortedData);
		} catch (err) {
			setError('Could not load authorities. Please try again.');
			console.error(err);
		} finally {
			setIsLoadingAuths(false);
		}
	};

	// Returns true if this authority row represents the current viewer.
	// Match on userId first (most reliable), fall back to phone number.
	const isSelf = (auth: Authority): boolean => {
		if (currentUserId && Number(auth.userId) === Number(currentUserId)) return true;
		if (currentAuthorityPhone && Number(auth.authority_phoneNumber) === Number(currentAuthorityPhone)) return true;
		return false;
	};

	const handleForward = async () => {
		if (!reportId || !selectedAuthorityId) return;

		setIsSubmitting(true);
		setError(null);
		try {
			const response = await fetch(`${BACKEND_URL}/api/forwardReport/${reportId}`, {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					targetAuthorityId: selectedAuthorityId,
					reason: reason.trim(),
				}),
			});

			// Report was deleted (here or from another device) while the modal was
			// open. Show the message, then drop the card and close.
			if (response.status === 404) {
				setError('This report no longer exists. It may have been deleted.');
				setTimeout(() => {
					onForwardSuccess(reportId);
					onClose();
				}, 1300);
				return;
			}

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData.message || 'Failed to forward report');
			}

			onForwardSuccess(reportId);
			onClose();
		} catch (err: any) {
			setError(err.message || 'An error occurred while forwarding.');
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	// Phone stays an exact substring match (fuzzing digits would surface the
	// wrong person); name/keywords go through the typo-tolerant matcher. A
	// phone hit is scored above fuzzy name/keyword hits since the searcher
	// typed something unambiguous.
	const phoneHits = authorities.filter((a) => String(a.authority_phoneNumber).includes(searchQuery.trim()));
	const nameOrKeywordHits = filterAuthorities(
		authorities,
		searchQuery,
		(a) => a.name,
		(a) => a.keywords
	);
	const filteredAuthorities = searchQuery.trim()
		? [...new Map([...phoneHits, ...nameOrKeywordHits].map((a) => [a.id, a])).values()]
		: nameOrKeywordHits;

	return (
		<div
			className='fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm'
			onClick={onClose}
		>
			<div
				ref={modalRef}
				className='bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col'
				style={{ maxHeight: '90vh' }}
				onClick={(e) => e.stopPropagation()}
			>
				<div className='flex-1 overflow-y-auto flex flex-col min-h-0'>
					{/* Header */}
					<div className='flex flex-col items-center justify-center pt-3 pb-3 px-4 border-b border-gray-100 bg-gray-50/50 text-center shrink-0'>
						<div className='w-12 h-12 mb-2 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center'>
							<Send size={24} />
						</div>
						<h2 className='text-xl font-bold text-gray-900 mb-1'>Forward Report</h2>
						<p className='text-xs sm:text-sm text-gray-500 font-medium w-full leading-snug mb-0'>
							When forwarded, you will no longer have any closing/reopening access to this report. The receiver will get
							the access.
						</p>
					</div>

					{/* Body */}
					<div className='px-4 pt-4 pb-4 flex flex-col shrink min-h-0 [@media(max-height:640px)]:shrink-0'>
						{error && (
							<div className='mb-3 p-3 bg-red-50 text-red-700 text-sm font-medium rounded-xl border border-red-100 flex items-center gap-2 shrink-0'>
								<ShieldAlert size={16} />
								{error}
							</div>
						)}

						<div className='flex flex-col shrink min-h-0 mb-4 [@media(max-height:640px)]:shrink-0'>
							<label className='shrink-0 block text-sm font-bold text-gray-700 mb-2'>Select Target Authority</label>
							<div className='relative mb-3 shrink-0'>
								<Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
								<input
									type='text'
									placeholder='Search by name or phone number...'
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className='w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all'
								/>
							</div>

							<div className='border border-gray-200 rounded-xl overflow-hidden flex flex-col shrink min-h-[80px] [@media(max-height:640px)]:max-h-[180px] [@media(max-height:640px)]:shrink-0'>
								<div className='overflow-y-auto flex-1 min-h-0'>
									{isLoadingAuths ? (
										<div className='flex flex-col items-center justify-center py-8 text-gray-400 gap-2'>
											<Loader2 className='w-6 h-6 animate-spin text-blue-500' />
											<span className='text-sm font-medium'>Loading authorities...</span>
										</div>
									) : filteredAuthorities.length === 0 ? (
										<div className='flex items-center justify-center py-8 text-sm text-gray-500 font-medium'>
											No authorities found
										</div>
									) : (
										<div className='divide-y divide-gray-100'>
											{filteredAuthorities.map((auth) => {
												const self = isSelf(auth);
												const selected = selectedAuthorityId === auth.id;
												return (
													<div
														key={auth.id}
														onClick={() => {
															if (self) return;
															if (selected) {
																setSelectedAuthorityId(null);
															} else {
																setSelectedAuthorityId(auth.id);
															}
														}}
														className={`px-3 py-3 flex items-center justify-between transition-colors ${
															self
																? 'bg-gray-50 opacity-60 cursor-not-allowed'
																: selected
																	? 'bg-blue-50 cursor-pointer'
																	: 'hover:bg-gray-50 cursor-pointer'
														}`}
														title={self ? 'You cannot forward a report to yourself' : undefined}
													>
														<div className='min-w-0 flex flex-col items-start text-left gap-2'>
															<div className='flex items-center gap-2'>
																<h4
																	className={`text-sm font-medium truncate leading-normal my-0 ${
																		self ? 'text-gray-500' : selected ? 'text-blue-700' : 'text-gray-900'
																	}`}
																>
																	{auth.name}
																</h4>
																{self && (
																	<span className='shrink-0 text-[10px] font-bold uppercase tracking-wide bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded leading-none'>
																		You
																	</span>
																)}
															</div>
															<p className='text-xs text-gray-500 text-left w-full leading-none mb-0'>
																+{auth.authority_phoneNumber}
															</p>
														</div>
														{/* Custom Radio Button */}
														<div
															className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
																self ? 'border-gray-300 bg-gray-100' : selected ? 'border-blue-500' : 'border-gray-300'
															}`}
														>
															{selected && !self && <div className='w-2.5 h-2.5 rounded-full bg-blue-500' />}
														</div>
													</div>
												);
											})}
										</div>
									)}
								</div>
							</div>
						</div>

						<div className='shrink-0 mb-4'>
							<label className='block text-sm font-bold text-gray-700 mb-2 text-center'>Reason</label>
							<div className='rounded-xl overflow-hidden border border-gray-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all'>
								<textarea
									placeholder='Why are you forwarding this report?'
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									className='w-full p-3 bg-gray-50 text-sm focus:outline-none resize-none h-20 overflow-y-auto block'
								/>
							</div>
						</div>

						{/* Action Buttons */}
						<div className='shrink-0 flex gap-3 w-full'>
							<button
								onClick={onClose}
								disabled={isSubmitting}
								className='flex-1 min-w-0 overflow-hidden px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors focus:outline-none'
							>
								Cancel
							</button>
							<button
								onClick={handleForward}
								disabled={!selectedAuthorityId || !reason.trim() || isSubmitting}
								className={`flex-1 min-w-0 overflow-hidden px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-semibold flex items-center justify-center gap-2 transition-colors focus:outline-none ${
									!selectedAuthorityId || !reason.trim() || isSubmitting
										? 'bg-blue-100 text-blue-400 cursor-not-allowed'
										: 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow'
								}`}
							>
								{isSubmitting ? (
									<>
										<Loader2 className='w-4 h-4 animate-spin' />
										Forwarding...
									</>
								) : (
									<>Confirm Forward</>
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ForwardReportModal;
